import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from '@tanstack/react-router'
import { ClipboardList, FileText, RefreshCcw } from 'lucide-react'
import type { ReactNode } from 'react'
import type { TaskEmptyContext, TaskEmptyIntro } from '@/routes/features/tasks/components/TaskPageLayout'
import { TaskEmptyState } from '@/routes/features/tasks/components/TaskEmptyState'
import { useProject, useTasks } from '@/lib/db/rxdb/hooks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useCreatePage } from '@/routes/features/pages/hooks/useCreatePage'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import useHideCompletedStore from '@/store/HideCompletedStore'
import { Button } from '@/components/ui/button'

/**
 * The two ways a project list can be empty without filters (design 33):
 * it never had a task (the add row becomes the empty state, 33a), or every
 * task is done and completed ones are hidden (33b, case 3).
 */
export function useProjectEmptyStates(projectId: string): {
  emptyIntro?: TaskEmptyIntro
  renderEmptyState: (context: TaskEmptyContext) => ReactNode
} {
  const { t } = useTranslation('tasks')
  const params = useParams({ strict: false })
  const workspaceSlug = params.workspaceSlug ?? ''
  const { project } = useProject(projectId)
  const { tasks } = useTasks({ isDeleted: false })
  const {
    open: { statusIds: openStatusIds },
  } = useStatuses()
  const hideCompleted = useHideCompletedStore((s) => s.hideCompleted)
  const toggleHideCompleted = useHideCompletedStore((s) => s.toggleHideCompleted)
  const { relativeTime } = useDateTimeFormatters()
  const createPage = useCreatePage({ projectId, projectKey: project?.projectKey ?? '', workspaceSlug })

  const stats = useMemo(() => {
    let total = 0
    let closed = 0
    let lastClosedOn = 0
    let maxSeqNo = 0
    for (const task of tasks) {
      if (task.projectId !== projectId) continue
      maxSeqNo = Math.max(maxSeqNo, task.seqNo ?? 0)
      if (task.parentId || task.isArchived) continue
      total += 1
      if (!openStatusIds.includes(task.statusId)) {
        closed += 1
        lastClosedOn = Math.max(lastClosedOn, task.updatedOn)
      }
    }
    return { total, closed, lastClosedOn, maxSeqNo }
  }, [tasks, projectId, openStatusIds])

  const emptyIntro = useMemo<TaskEmptyIntro | undefined>(() => {
    if (stats.total > 0 || !project) return undefined
    const key = project.projectKey
    const tip = (icon: ReactNode, label: string, hint: string, onClick?: () => void) => (
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className="flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-[13px] transition-colors enabled:hover:bg-secondary disabled:opacity-50"
      >
        <span className="text-muted-foreground">{icon}</span>
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">· {hint}</span>
      </button>
    )
    return {
      title: t('empty.projectIntroTitle', { name: project.name }),
      description: key
        ? t('empty.projectIntroBody', { key, id: `${key}-${stats.maxSeqNo + 1}` })
        : t('empty.projectIntroBodyNoKey'),
      renderTips: ({ openAddTask }) => (
        <div className="flex flex-wrap gap-2">
          {tip(<ClipboardList className="size-3.5" />, t('empty.tipPasteList'), t('empty.tipPasteListHint'), openAddTask)}
          {tip(<FileText className="size-3.5" />, t('empty.tipBrief'), t('empty.tipBriefHint'), () => void createPage())}
          {/* A cycle needs tasks to plan, so it waits for the first one. */}
          {tip(<RefreshCcw className="size-3.5" />, t('empty.tipCycle'), t('empty.tipCycleHint'))}
        </div>
      ),
      footer: (
        <Trans
          t={t}
          i18nKey="empty.pressC"
          components={{ kbd: <kbd className="rounded border bg-secondary px-1 font-mono text-[11px] text-foreground" /> }}
        />
      ),
    }
  }, [stats.total, stats.maxSeqNo, project, t, createPage])

  const renderEmptyState = ({ openAddTask }: TaskEmptyContext) => {
    if (hideCompleted && stats.total > 0 && stats.closed === stats.total) {
      return (
        <TaskEmptyState
          title={t('empty.allDoneTitle', { count: stats.total })}
          description={`${t('empty.allDoneLastClosed', { when: relativeTime(new Date(stats.lastClosedOn)) })} ${t('empty.completedHidden')}`}
          actions={
            <>
              <Button size="sm" onClick={toggleHideCompleted}>
                {t('empty.showCompleted')}
              </Button>
              <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={openAddTask}>
                {t('empty.addTask')}
              </Button>
            </>
          }
        />
      )
    }
    return <TaskEmptyState />
  }

  return { emptyIntro, renderEmptyState }
}
